import React, { useCallback, useState } from 'react';
import { createStyles, Theme, makeStyles, Collapse } from '@material-ui/core';

/* import components */
import { CustomFadeProperties } from '../../helpers/CustomFade';
import { FrameControls } from './FrameControls';
import { SnapshotsList } from './SnapshotControls';

// hooks
import { PlaybackButtons } from './PlaybackButtons';
import { FullscreenButton } from './FullscreenButton';
import { VolumeControls } from './VolumeControls';
import {
    PlaybackSlider,
    WithAnnotationPlaybackSliderProps
} from './PlaybackSlider';
import { PlaybackTimeDisplay } from './PlaybackTimeDisplay';
import {
    useVideoPlayerKeyboardShortcuts
} from './hooks/useVideoPlayerKeyboardShortcuts';
import { useClassName } from '../../utils/useClassName';
import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import Loader from '@cube3/cubicle/src/core/atoms/Loader/Loader';
import { AddCommentButton } from './AddCommentButton';
import { useCurrentShareLink } from '../../../../state/src/redux/components/Hooks/useCurrentShareLink';


export const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        '@keyframes fade-in': {
            '0%': {
                opacity: 0
            },
            '100%': {
                opacity: 1
            }
        },
        mobile: {},
        container: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            bottom: '0',
            padding: theme.customSpacing.padding[8],
            paddingBottom: theme.customSpacing.padding[20],
            width: 'calc(100% - 16px)'
        },
        showMoreContainer: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: theme.customSpacing.padding[8],
            paddingBottom: theme.customSpacing.padding[20],
            height: 200,
            width: 'calc(100% - 16px)',
            bottom: 0
        },
        fullscreen: {
            color: theme.customPalette.primary.textWhiteHighEmphasis,
            backgroundColor: 'transparent'
        },
        buttonContainer: {
            marginTop: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        showMoreButtonContainer: {
            marginTop: theme.customSpacing.margin[8],
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        showMoreTopSection: {
            display: 'flex',
            width: '100%',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center'
        },

        leftSection: {
            display: 'flex',
            alignItems: 'center'
        },
        frameControl: {
            // visibility: 'hidden',
            opacity: '0.2',
            pointerEvents: 'none'
        },
        leftButtonsContainer: {
            width: 'auto',
            marginRight: 0,
            display: 'flex',
            justifyContent: 'space-between'
        },
        durationContainer: {
            display: 'flex',
            justifyContent: 'space-evenly',
            alignItems: 'center',
            width: 'auto',
            maxWidth: 'fit-content',
            marginLeft: 16
        },
        rightSection: {
            display: 'flex',
            alignItems: 'center'
        },
        scrim: (showSnapshotsList) => {
            return {
                pointerEvents: 'none',
                position: 'absolute',
                height: '92px',
                left: 0,
                bottom: showSnapshotsList ? 300 : 0,
                right: 0,
                background: showSnapshotsList ? 'transparent' : theme.customPalette.surfaceAccent.onBase.scrim3,
                zIndex: -1,
                transition: 'bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                '&.loading': {
                    marginBottom: '-160px'
                }
            };
        },
        seeking: {
            filter: 'grayscale(60%) brightness(0.6)',
            cursor: 'wait',
            '& *': {
                cursor: 'wait'
            }
        },
        buffering: {},
        spinner: {
            position: 'absolute',
            top: 0,
            right: 24,
            animation: '$fade-in 0.75s ease-in 0.75s 1',
            animationFillMode: 'both',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.32)',
            width: 20,
            height: 20,
            '& > div': {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%,-50%)'
            }
        }
    })
);

interface ControlsProps
    extends CustomFadeProperties,
    WithAnnotationPlaybackSliderProps {
    playing: boolean;
    duration: number;
    playedSeconds: number;
    loop: boolean;
    volume: number;
    playPause: () => void;
    onSeekChange: (event: Object, value: any) => void;
    toggleLoop: () => void;
    jumpBack: () => void;
    jumpForward: () => void;
    stepFrame?: (step: number) => void;
    setVolume: (value: number) => void;
    toggleFullscreenAction: () => void;
    playedTime: string;
    played: number;
    durationDateFormat: string;
    fullscreen: boolean;
    fullscreenControls?: JSX.Element;
    replay: () => void;
    toggleMute: (newVal?: boolean) => void;
    muted: boolean;
    otherModalsActive: boolean;
    frameRate?: number;
    createSnapshot: () => void;
    goToTime?: (sec: number) => void;
    canTakeSnapshot: boolean;
    snapshots: any;
    activeFrame: number;
    error: string;
    seeking: boolean;
    buffering: boolean;
    buffered?: TimeRanges;
    stripSrc?: string;
    // review tool
    drawingBarToggled?: boolean;
    toggleDrawingBar?: () => void;
    setThumbnail: () => void;
    saveThumbnailStatus: number | 'loading';
    //Navigation arrows for preview view
    showNavArrows?: boolean;
    setShowNavArrows?: React.Dispatch<React.SetStateAction<boolean>>;
}

const Controls: React.FC<ControlsProps> = React.memo((props) => {
    const {
        playing,
        duration,
        playedSeconds,
        loop,
        volume,
        playPause,
        onSeekChange,
        toggleLoop,
        jumpBack,
        jumpForward,
        stepFrame,
        setVolume,
        playedTime,
        played,
        durationDateFormat,
        toggleFullscreenAction,
        fullscreen,
        fullscreenControls,
        replay,
        toggleMute,
        muted,
        otherModalsActive,
        focus,
        // for steping through a video frame by frame
        frameRate,
        createSnapshot,
        canTakeSnapshot,
        snapshots,
        goToTime,
        activeFrame,
        error,
        seeking,
        buffering,
        buffered,
        stripSrc,
        // review tools
        toggleDrawingBar,
        drawingBarToggled,
        comments,
        onClickComment,
        setThumbnail,
        saveThumbnailStatus,
        //
        showNavArrows,
        setShowNavArrows
    } = props;

    useVideoPlayerKeyboardShortcuts({
        focus,
        otherModalsActive,
        playPause,
        toggleMute,
        jumpBack,
        jumpForward,
        toggleLoop,
        toggleFullscreenAction,
        stepFrame
    });

    const [showSnapshotsList, setShowSnapshotsList] = useState(false);
    const [lastCaptureSeconds, setLastCaptureSeconds] = useState<number[]>([]);
    const canCapture = !lastCaptureSeconds.includes(playedSeconds);

    const handleClickSnapshot = useCallback(() => {
        if (!canCapture) return;
        createSnapshot();
        setLastCaptureSeconds((prev) => [...prev, playedSeconds]);
    }, [canCapture, createSnapshot, playedSeconds]);

    const toggleSnapshotsList = useCallback(() => {
        if (snapshots && snapshots.length === 0 && playedSeconds === 0) {
            playPause();
            playPause();
        }
        setShowSnapshotsList(!showSnapshotsList);
    }, [playPause, playedSeconds, showSnapshotsList, snapshots]);

    const classes = useStyles(showSnapshotsList);

    const handleClickDrawingBar = useCallback(() => {
        toggleDrawingBar();
        if (playing) {
            playPause();
        }
    }, [playing, toggleDrawingBar, playPause]);


    /**mobile functions */
    const [showMoreControls, setShowMoreControls] = useState(false);
    const typeOfView = useCurrentShareLink()?.type;
    //if revie or share show comment button
    const useComment = typeOfView === 'share';

    const handleShowMore = useCallback(() => {
        if (playing) { playPause() }
        setShowMoreControls(!showMoreControls);
        setShowSnapshotsList(false);
        //if type is share dont set the preview nav arrows
        if (typeOfView != 'share') { setShowNavArrows(!showNavArrows); }
    }, [showMoreControls, playing, playPause, showNavArrows]);

    const closeShowMore = useCallback(() => {
        setShowMoreControls(false)
        setShowSnapshotsList(false);
        if (typeOfView != 'share') { setShowNavArrows(!showNavArrows); }
    }, [showMoreControls, showNavArrows]);


    const handleAddComment = useCallback(() => {
        setShowMoreControls(false)
    }, [playing, playPause]);


    return (
        <>
            <div
                className={useClassName(
                    showMoreControls ? classes.showMoreContainer : classes.container,
                    fullscreen && classes.fullscreen,
                    seeking && classes.seeking,
                    buffering && classes.buffering
                )}>
                {(seeking || buffering) && (
                    <div className={classes.spinner}>
                        <Loader variant="indeterminate" size={SizeVariants.sm} />
                    </div>
                )}

                <div
                    className={`${classes.scrim} ${seeking || buffering ? 'loading' : ''}`} />

                {fullscreen && fullscreenControls}

                <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <PlaybackButtons
                        finished={duration && playedSeconds === duration}
                        playing={playing}
                        replay={replay}
                        playPause={playPause}
                        showOnlyPlay={true}
                    />
                    <PlaybackSlider
                        played={played}
                        playing={playing}
                        duration={duration}
                        onSeekChange={onSeekChange}
                        buffered={buffered}
                        stripSrc={stripSrc}
                        // review tools
                        comments={comments}
                        onClickComment={onClickComment}
                        frameRate={frameRate}
                    />
                    <VolumeControls
                        volume={volume}
                        muted={muted}
                        toggleMute={toggleMute}
                        setVolume={setVolume}
                        shortCutTip={''}
                    />
                </div>

                <div className={showMoreControls ? classes.showMoreButtonContainer : classes.buttonContainer} data-prevent-deselect>
                    {showMoreControls &&
                        <div className={classes.showMoreTopSection}>
                            <div className={classes.durationContainer}>
                                <PlaybackTimeDisplay
                                    playedTime={playedTime}
                                    durationDateFormat={durationDateFormat}
                                />
                            </div>
                            {frameRate &&
                                <div className={playing ? classes.frameControl : ''} >
                                    <FrameControls
                                        stepFrame={stepFrame}
                                        activeFrame={activeFrame}
                                        onToggle={toggleSnapshotsList}
                                        showSnapshotsList={showSnapshotsList}
                                        canTakeSnapshot={false}//remove snapshot button from 1st row of controls
                                        shortCutTips={''}
                                    />
                                </div>
                            }
                        </div>
                    }


                    <div className={classes.buttonContainer}>
                        <div className={classes.leftSection}>
                            {showMoreControls &&
                                <div className={classes.leftButtonsContainer}>
                                    <PlaybackButtons
                                        finished={duration && playedSeconds === duration}
                                        playing={playing}
                                        loop={loop}
                                        jumpBack={jumpBack}
                                        toggleLoop={toggleLoop}
                                        replay={replay}
                                        playPause={playPause}
                                        shortCutTips={''}
                                    />
                                </div>
                            }
                            {!showMoreControls &&
                                <div className={classes.durationContainer}>
                                    <PlaybackTimeDisplay
                                        playedTime={playedTime}
                                        durationDateFormat={durationDateFormat}
                                    />
                                </div>}
                        </div>

                        {showMoreControls && canTakeSnapshot &&
                            //show snapshot button in second row of controls 
                            <FrameControls
                                onToggle={toggleSnapshotsList}
                                showSnapshotsList={showSnapshotsList}
                                canTakeSnapshot={canTakeSnapshot}
                                showFramesteps={false}
                            />
                        }



                        {useComment && showMoreControls &&
                            <div style={{ display: 'flex' }}>
                                {toggleDrawingBar &&
                                    <Button
                                        aria-label="toggle drawing bar"
                                        iconLeft={'gesture'}
                                        background="secondary"
                                        buttonStyle="ghost"
                                        onClick={handleClickDrawingBar}
                                        size={SizeVariants.lg}
                                    />}
                                <AddCommentButton onAddComment={handleAddComment} playing={playing} drawingBarToggled={drawingBarToggled} />
                            </div>
                        }



                        <div className={classes.rightSection}>
                            {!showMoreControls && useComment &&//on close more controls
                                <AddCommentButton onAddComment={handleAddComment} playing={playing} drawingBarToggled={drawingBarToggled}
                                    toggleDrawingBar={toggleDrawingBar} />
                            }

                            <Button
                                aria-label="more control options"
                                iconLeft={'more'}
                                background="secondary"
                                buttonStyle="ghost"
                                onClick={handleShowMore}
                                size={SizeVariants.lg}
                            />

                            <div onClick={closeShowMore}>
                                <FullscreenButton
                                    fullscreen={fullscreen}
                                    toggleFullscreenAction={toggleFullscreenAction}
                                    shortCutTip={''}
                                />
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            {canTakeSnapshot && (
                <Collapse in={showSnapshotsList}>
                    <SnapshotsList
                        snapshots={snapshots}
                        error={error}
                        canCapture={canCapture}
                        loading={buffering || seeking}
                        onClickSnapshot={handleClickSnapshot}
                        onClickThumbnail={goToTime}
                        saveThumbnail={setThumbnail}
                        saveThumbnailStatus={
                            typeof saveThumbnailStatus === 'number' &&
                                Math.abs(saveThumbnailStatus - playedSeconds) <
                                1 / (frameRate || 25)
                                ? 'failed'
                                : saveThumbnailStatus === 'loading'
                                    ? 'loading'
                                    : undefined
                        }
                        playedSeconds={playedSeconds}
                        showSnapshotsList={showSnapshotsList}
                    />
                </Collapse>
            )}
        </>
    );
});

export const VideoPreviewControlsMobile = Controls;
